import { useEffect, useRef } from 'react';
import { getSocket } from '../services/socketService';

export interface ProgressSocketEvent {
  assignmentId: string;
  stepId?: string;
  status?: string;
  completedAt?: string | null;
}

const EVENTS = [
  'progress:updated',
  'progress:completed',
  'progress:reopened',
  'assignment:completed',
] as const;

/**
 * Subscribe to real-time progress events (#6). The backend auto-joins each
 * client to its own `user:{id}` room on connect, so there's nothing to join —
 * we just listen. The callback is held in a ref so passing an inline function
 * doesn't re-subscribe on every render.
 *
 * Pair this with the page's existing on-load fetch (which stays as the source
 * of truth + fallback): events simply trigger a refetch, so a missed event
 * (disconnect, multi-worker) self-heals on the next load/refresh.
 */
export function useProgressEvents(
  onEvent: (event: string, payload: ProgressSocketEvent) => void,
): void {
  const cb = useRef(onEvent);
  cb.current = onEvent;

  useEffect(() => {
    const socket = getSocket();
    if (!socket.connected) socket.connect();

    const handlers = EVENTS.map((evt) => {
      const handler = (payload: ProgressSocketEvent) => cb.current(evt, payload);
      socket.on(evt, handler);
      return [evt, handler] as const;
    });

    return () => {
      handlers.forEach(([evt, handler]) => socket.off(evt, handler));
    };
  }, []);
}
